import {
    call,
    fork,
    put,
    select,
    take,
    takeLatest,
    race,
    delay,
} from 'redux-saga/effects';
import { createSelector } from 'reselect';
import {
    ActionCreatorType,
    createObjectReducer,
    createUpdateAction,
} from '@core/store/actions';
import { createType } from './core';
import {
    IBaseHandler,
    SimpleSelector,
} from './props';
import { safe } from '../utils/globalUtils';
import { safeSaga } from './utils';

/**
 * Хендлер для управления данных во внешнем хранилище.
 * Позволяет читать и обновлять данные в хранилище.
 *
 * @category Duck Handlers
 */
export interface IStoreHandler<TModel> extends IBaseHandler {
    /**
     * Селектор для получения данных.
     * @group Selectors
     */
    selector: SimpleSelector<TModel>;
    /**
     * @event
     * Срабатывает во время обновления данных.
     */
    UPDATE_ACTION: string;

    /**
     * @event
     * Срабатывает во время успешной инициализации стора.
     */
    INIT_ACTION?: string;

    /**
     * Действие для обновления данных в хранилище.
     * Используете `undefined` чтобы сбросить данные в начальное состояние.
     */
    update: ActionCreatorType<TModel>;

    /**
     * Флаг, означающий, что стор проинициализирован и готов к работе
     */
    isReady?: SimpleSelector<boolean>;
}

/**
 * Базовые настройки хендлера.
 */
export interface IStoreHandlerBasicConfig<TModel> {
    /**
     * Функция для получения начального состояния.
     */
    getInitialState?: () => Partial<TModel>;
    /**
     * Флаг отладки данных.
     */
    echo?: boolean;
}

/**
 * Базовые настройки управления хранилищем.
 */
export interface IStoreHandlerPersistConfig<TModel> {
    /**
     * Событие для начальной загрузки данных из хранилища.
     */
    onLoad?: () => Promise<Partial<TModel> | undefined>|Partial<TModel> | undefined;
    /**
     * Событие для сохранения данных в хранилище.
     */
    onSave?: (data: Partial<TModel>) => Promise<void>|void;
}

export interface IStoreHandlerConfig<TModel> extends IStoreHandlerBasicConfig<TModel>, IStoreHandlerPersistConfig<TModel> {}

/**
 * Внутренний стейт хэндлера
 */
interface IInternalState {
    /**
     * Готов ли store к работе
     */
    isReady: boolean;
}

/**
 * Абстрактная модель хранения данных.
 * С помощью конфигурации позволяет реализовать хранение данных во внешнем хранилище с помощью событий `onSave` и `onLoad`.
 *
 * Без указания настроек `onSave` и `onLoad` хендлер будет вести себя как обычный слайс доступа к данным в Redux.
 *
 * @param prefix Префикс duck модуля
 * @param name Имя хендлера внутри модуля
 * @param config Дополнительные настройки хендлера
 * @returns Новый экземпляр управления хранилищем данных
 *
 * @category Duck Handlers
 *
 * @see {@link useStoreHandler}
 * @see {@link localStoreHandler}
 * @see {@link sessionStoreHandler}
 * @see {@link asyncStorageStoreHandler}
 */
export function storeHandler<TModel = any>(prefix: string, name: string, config: IStoreHandlerConfig<TModel> = {}): IStoreHandler<TModel> {
    const id = `${prefix}/${name}`;
    const actionName = name.toUpperCase();

    const UPDATE_ACTION = createType(prefix, `UPDATE_${actionName}`);
    const update = createUpdateAction<TModel>(UPDATE_ACTION);

    const STORE_INIT_ACTION = createType(prefix, `INIT_${actionName}`);
    const init = createUpdateAction<void>(STORE_INIT_ACTION);

    const getDuck = state => state[prefix];
    const selector = createSelector<any, TModel>(getDuck, data => data[name] as TModel);

    const internalStateName = `${name}__internal`;
    const INTERNAL_UPDATE_ACTION = createType(prefix, `INTERNAL_UPDATE_${actionName}`);
    const internalUpdate = createUpdateAction<IInternalState>(INTERNAL_UPDATE_ACTION);

    let cachedLoadedState: Partial<TModel> | undefined;

    const isReady = createSelector<any, boolean>(
        getDuck,
        data => (data[internalStateName] as IInternalState)?.isReady ?? false
    );

    const getInitialState = (): Partial<TModel> => {
        const defaultInitialState: TModel = typeof config.getInitialState === 'function'
            ? safe(() => config.getInitialState()) ?? {}
            : {} as TModel;

        return defaultInitialState;
    };

    const getInitialInternalState = (): IInternalState => ({
        isReady: false,
    });

    const reducer = createObjectReducer<Partial<TModel>>(UPDATE_ACTION, getInitialState);
    const internalReducer = createObjectReducer<IInternalState>(
        INTERNAL_UPDATE_ACTION,
        getInitialInternalState
    );

    const reducerInfo = {
        [name]: reducer,
        [internalStateName]: internalReducer,
    };

    function* setIsReadyState(ready: boolean) {
        yield put(internalUpdate({ isReady: true }));

        if (ready) {
            yield put(init());

            if (config.echo) {
                console.debug(`STORE ${id} INITIALIZED`);
            }
        }
    }

    function* initialStoreLoadSaga() {
        // Сначала сбрасываем состояние готовности
        yield call(setIsReadyState, false);

        if (typeof config.onLoad !== 'function') {
            return;
        }

        if (config.echo) {
            if (!cachedLoadedState) {
                console.debug(`STORE ${id} LOADING`, prefix, actionName);
            }
        }

        const loadedState = yield call(config.onLoad);
        if (loadedState) {
            cachedLoadedState = loadedState;
            yield put({
                type: UPDATE_ACTION,
                value: loadedState,
                source: 'onLoad',
            });
        }

        if (config.echo) {
            console.debug(`STORE ${id} LOADED`, prefix, actionName, JSON.stringify(loadedState) ?? '<undefined>');
        }

        // Устанавливаем флаг готовности независимо от наличия onLoad
        yield call(setIsReadyState, true);
    }

    function* onUpdateActionSaga({
        type,
        value,
        source,
    }: { type: string; value: Partial<TModel>; source: string }) {
        if (source === 'onLoad') {
            return;
        }

        if (value === undefined) {
            if (config.echo) {
                console.debug(`STORE ${id} CLEAR`);
            }

            cachedLoadedState = null;
            if (typeof config.onSave === 'function') {
                yield call(config.onSave, undefined);
            }

            return;
        }

        const storeState = yield select(selector);
        if (config.echo) {
            console.debug(`STORE ${id} SAVE`, JSON.stringify(storeState) ?? '<undefined>');
        }

        if (typeof config.onSave === 'function') {
            yield call(config.onSave, storeState);
        }
    }

    const effects = [
        takeLatest(UPDATE_ACTION, safeSaga(onUpdateActionSaga, id)),
        fork(safeSaga(initialStoreLoadSaga, id)),
    ];

    return {
        UPDATE_ACTION,
        INIT_ACTION: STORE_INIT_ACTION,
        update,
        selector,
        reducer,
        reducerInfo,
        effects,
        isReady,
    };
}

/**
 * Дожидается готовности хранилища к чтению или обновлению данных.
 *
 * @param handler Обработчик хранилища (storeHandler)
 * @param timeoutMs Опциональное время таймаута в миллисекундах (по умолчанию 30000мс)
 * @returns true, если хранилище готово; false, если превышен таймаут
 *
 * @throws Error, если в качестве handler передан не storeHandler
 *
 * @example
 * ```typescript
 * function* mySaga() {
 *   // Дождаться готовности стора пользователя
 *   const isReady = yield call(waitStoreForReady, store);
 *
 *   if (isReady) {
 *     // Продолжить работу с данными
 *   } else {
 *     // Обработать ситуацию превышения таймаута
 *     yield put(addToastError('Не удалось загрузить данные пользователя'));
 *   }
 * }
 * ```
 */
export function* waitStoreForReady<TModel>(
    handler: IStoreHandler<TModel>,
    timeoutMs = 30000
): Generator<any, boolean, any> {
    if (!handler) {
        throw new Error('waitStoreForReady: требуется валидный storeHandler');
    }

    // Проверяем текущее состояние готовности
    const isReady = yield select(handler.isReady);

    // Если уже готов, возвращаем true немедленно
    if (isReady) {
        return true;
    }

    // Иначе ждем либо события INIT_ACTION, либо таймаута
    const {
        initAction,
    } = yield race({
        initAction: take(handler.INIT_ACTION),
        timeout: delay(timeoutMs),
    });

    // Возвращаем true, если получили событие инициализации, и false, если таймаут
    return Boolean(initAction);
}


